import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Circle, ArrowRight, EyeOff, TrendingUp } from "lucide-react";
import { api, fmtError } from "@/lib/api";

/**
 * Composant réutilisable — carte de complétude prestataire.
 * Affiche : barre de progression + message d'impact + liste d'actions
 * ordonnée par impact (weight décroissant), items non-cochés en tête.
 */
export default function ProfileCompletionCard({ onLoaded }) {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/prestataires/me/completion");
      setData(data);
      onLoaded && onLoaded(data);
    } catch (err) {
      toast.error(fmtError(err));
    }
  }, [onLoaded]);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const { percent, items, visible_in_search } = data;

  const missing = items.filter((i) => !i.done).sort((a, b) => b.weight - a.weight);
  const complete = items.filter((i) => i.done);
  const done = percent >= 100;

  const barColor = done ? "bg-[#0F7A47]" : percent >= 60 ? "bg-[#1B3A5C]" : "bg-[#E8710A]";

  return (
    <div className="card p-4" data-testid="profile-completion-card">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <h2 className="text-lg font-semibold text-[#1F2933]">
          Profil complété à <span data-testid="completion-percent">{percent} %</span>
        </h2>
        <span className="text-[11px] uppercase tracking-wider text-[#3E4C59]">
          Impact sur votre visibilité
        </span>
      </div>

      <div className="mt-2 w-full h-2 bg-[#E4E7EB] rounded-full overflow-hidden">
        <div
          className={`h-full ${barColor} transition-all`}
          style={{ width: `${Math.min(100, Math.max(2, percent))}%` }}
          data-testid="completion-bar"
        />
      </div>

      <div
        className={`mt-3 p-2.5 rounded-[4px] text-sm ${
          visible_in_search
            ? "bg-[#F0FBF5] text-[#0F7A47] border-l-4 border-[#0F7A47]"
            : "bg-[#FEF2F2] text-[#8B2A22] border-l-4 border-[#B7443A]"
        }`}
        data-testid="completion-impact-msg"
      >
        {visible_in_search ? (
          <span className="flex items-center gap-1.5">
            <TrendingUp size={14} /> Les profils complets apparaissent en premier dans les recherches des entreprises.
          </span>
        ) : (
          <span className="flex items-center gap-1.5">
            <EyeOff size={14} /> Votre profil n'apparaît pas encore dans les résultats de recherche. Complétez-le à 40 % minimum pour être visible.
          </span>
        )}
      </div>

      {missing.length > 0 && (
        <ul className="mt-3 space-y-1.5" data-testid="completion-actions">
          {missing.map((it) => (
            <li key={it.key}>
              <Link
                to={it.action}
                className="flex items-center justify-between gap-2 p-2 rounded-[4px] border border-[#E4E7EB] hover:bg-[#F5F6F7] transition-colors text-sm"
                data-testid={`completion-action-${it.key}`}
              >
                <span className="flex items-center gap-2 text-[#1F2933]">
                  <Circle size={14} className="text-[#3E4C59] shrink-0" />
                  <span>{it.label}</span>
                  <span className="text-[#E8710A] font-semibold">+{it.weight} %</span>
                </span>
                <ArrowRight size={14} className="text-[#3E4C59] shrink-0" />
              </Link>
            </li>
          ))}
        </ul>
      )}

      {complete.length > 0 && (
        <details className="mt-3 text-xs text-[#3E4C59]">
          <summary className="cursor-pointer">
            {complete.length} élément(s) complété(s)
          </summary>
          <ul className="mt-1 space-y-1">
            {complete.map((it) => (
              <li key={it.key} className="flex items-center gap-2">
                <CheckCircle2 size={12} className="text-[#0F7A47]" />
                <span>{it.label}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
